import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Plus, Tag as TagIcon, Info } from 'lucide-react';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import TagFormModal from '@components/tags/tag-form-modal.tsx';
import { useGetTags } from '@utils/api/organizations/tags';
import { useCreateTagAssignment } from '@utils/api/tag-assignments';
import { Tag } from '@utils/types/organization/tag.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { formatTimeClean } from '@utils/time.ts';

interface TagPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  documentId: string;
  selectedText: string;
  indexFrom?: string;
  indexTo?: string;
}

// "3 words · 00:12 – 00:15" — helps the user judge selection scope before
// committing to a tag. index_from/index_to are millisecond strings.
const formatSelectionMeta = (
  text: string,
  indexFrom: string | undefined,
  indexTo: string | undefined,
  t: (k: string) => string
): string => {
  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
  const wordLabel =
    wordCount === 1 ? t('word') || 'word' : t('words') || 'words';
  const from = Number(indexFrom);
  const to = Number(indexTo);
  if (Number.isFinite(from) && Number.isFinite(to) && to >= from) {
    return `${wordCount} ${wordLabel} · ${formatTimeClean(from / 1000)} – ${formatTimeClean(to / 1000)}`;
  }
  return `${wordCount} ${wordLabel}`;
};

export default function TagPickerModal({
  isOpen,
  onClose,
  sessionId,
  documentId,
  selectedText,
  indexFrom,
  indexTo
}: TagPickerModalProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [query, setQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [submittingTagId, setSubmittingTagId] = useState<number | null>(null);

  const { data, isLoading } = useGetTags({
    offset: 0,
    limit: 200,
    sort: 'name',
    direction: 'asc'
  });

  const createAssignment = useCreateTagAssignment();

  const tags = useMemo<Tag[]>(() => {
    const list = data?.result ?? [];
    if (!query.trim()) return list;
    const needle = query.toLowerCase();
    return list.filter(tag => tag.name.toLowerCase().includes(needle));
  }, [data, query]);

  const handleApply = (tag: Tag) => {
    if (!selectedText) {
      toast.error(t('please-select-the-text-to-tag') || 'Select text first');
      return;
    }

    setSubmittingTagId(tag.id);
    createAssignment.mutate(
      {
        session_id: sessionId,
        document_id: documentId,
        tag_id: tag.id,
        selected_text: selectedText,
        index_from: indexFrom,
        index_to: indexTo
      },
      {
        onSuccess: () => {
          toast.success(t('tag-applied') || 'Tag applied');
          setSubmittingTagId(null);
          onClose();
        },
        onError: () => {
          toast.error(t('failed-to-apply-tag') || 'Failed to apply tag');
          setSubmittingTagId(null);
        }
      }
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen && !isCreateOpen}
        onClose={onClose}
        size="small"
        title={t('tag-selection') || 'Tag selection'}
        caption={
          t('apply-a-tag-to-the-selected-text') ||
          'Pick a tag to apply to the selected text.'
        }
        footerActions={[
          <Button
            key="cancel"
            onClick={onClose}
            label={t('cancel')}
            size="small"
            variant="smoke"
          />
        ]}
      >
        <div className="w-full space-y-3">
          {selectedText && (
            <div
              className="rounded-2xl border-2 px-3 py-2.5"
              style={{
                borderColor: getColour('activeText'),
                backgroundColor: getColour('appSoft')
              }}
            >
              <div
                className="flex items-center justify-between gap-2 mb-1.5"
                style={{ color: getColour('normalText') }}
              >
                <span className="text-xs font-semibold uppercase tracking-wide">
                  {t('you-are-about-to-tag') || 'You are about to tag'}
                </span>
                <span
                  className="text-[11px] font-mono"
                  style={{ color: getColour('inactiveText') }}
                >
                  {formatSelectionMeta(selectedText, indexFrom, indexTo, t)}
                </span>
              </div>
              <div
                className="text-sm italic line-clamp-3"
                style={{ color: getColour('normalText') }}
              >
                &ldquo;{selectedText}&rdquo;
              </div>
              <div
                className="mt-1.5 flex items-start gap-1 text-[11px]"
                style={{ color: getColour('inactiveText') }}
              >
                <Info size={11} className="mt-0.5 flex-shrink-0" />
                <span>
                  {t('selection-snaps-to-words-hint') ||
                    'Selections snap to whole words. If this looks wrong, cancel and re-select more precisely.'}
                </span>
              </div>
            </div>
          )}

          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('search-tags') || 'Search tags'}
          />

          <div
            className="max-h-64 overflow-y-auto rounded-2xl border"
            style={{ borderColor: getColour('border') }}
          >
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader height={10} width={3} />
              </div>
            ) : tags.length === 0 ? (
              <div
                className="px-3 py-6 text-center text-xs"
                style={{ color: getColour('inactiveText') }}
              >
                {query
                  ? t('no-tags-match-search') || 'No matching tags'
                  : t('no-tags-yet') || 'No tags yet'}
              </div>
            ) : (
              <ul>
                {tags.map((tag, idx) => {
                  const isSubmitting = submittingTagId === tag.id;
                  return (
                    <li
                      key={tag.id}
                      style={
                        idx === 0
                          ? undefined
                          : {
                              borderTop: `1px solid ${getColour('hairline')}`
                            }
                      }
                    >
                      <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => handleApply(tag)}
                        className="group w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-primary/5 transition-colors disabled:opacity-60"
                      >
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="inline-block h-3 w-3 rounded-full"
                            style={{
                              backgroundColor:
                                tag.color || getColour('inactiveText'),
                              border: `1px solid ${getColour('border')}`
                            }}
                          />
                          <span
                            className="text-sm font-medium opacity-75 group-hover:opacity-100 transition-opacity"
                            style={{ color: getColour('normalText') }}
                          >
                            {tag.name}
                          </span>
                        </span>
                        {isSubmitting && <Loader height={8} width={2} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <Button
            onClick={() => setIsCreateOpen(true)}
            variant="outline"
            size="small"
            icon={<Plus size={14} />}
            label={t('create-new-tag') || 'Create new tag'}
          />

          {tags.length === 0 && !isLoading && !query && (
            <div
              className="flex items-center gap-2 text-xs"
              style={{ color: getColour('inactiveText') }}
            >
              <TagIcon size={12} />
              {t('create-your-first-tag-to-mark-selections') ||
                'Create your first tag to start marking selections.'}
            </div>
          )}
        </div>
      </Modal>

      <TagFormModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        tag={null}
      />
    </>
  );
}
